import {useRef} from 'react';
import {useStore} from 'react-redux';

import {useUpdateWorkshopInterestVehicleDetailsMutation} from '@dms/api/workshop';
import {handleApiError} from '@dms/shared';

import {getSafely} from 'shared/web';

import {VehicleDetailsFormType} from '../types';
import {transformFormToVehicleDetails} from '../utils/transformCustomerInquiry';

export function useVehicleDetailsUpdate(interestId: string) {
  const store = useStore();
  const pendingValuesRef = useRef<VehicleDetailsFormType | null>(null);

  const cacheKey = `update-vehicle-details-${interestId}`;
  const [updateVehicleDetails] = useUpdateWorkshopInterestVehicleDetailsMutation({
    fixedCacheKey: cacheKey,
  });

  // Read mutation status directly from Redux store at call time.
  // Avoids stale closure issues caused by AutoSave's debounced callbacks.
  const isUpdateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    return getSafely(mutations, cacheKey)?.status === 'pending';
  };

  const onVehicleDetailsUpdate = (values: VehicleDetailsFormType) => {
    if (isUpdateInProgress()) {
      pendingValuesRef.current = {...pendingValuesRef.current, ...values};
      return;
    }

    updateVehicleDetails({
      interestId,
      updateVehicleDetailsRequestBody: transformFormToVehicleDetails(values),
    })
      .unwrap()
      .then(() => {
        if (pendingValuesRef.current) {
          const pending = pendingValuesRef.current;
          pendingValuesRef.current = null;
          onVehicleDetailsUpdate(pending);
        }
      })
      .catch(handleApiError);
  };

  return {onVehicleDetailsUpdate};
}
